import { useEffect, useState, ChangeEvent } from 'react'
import { useSearchParams } from 'react-router-dom'

import { isStringTheme, isStringLanguage } from '../tools/typeGuards'
import { iziplayAuth } from '../tools/api'
import languages from '../tools/languages.json'
import utils from '../tools/utils'

import './settings.css'
import { changeInterfaceColor, saveTheme, setFont } from '../state/theme'
import { displayNotification, getLang, setCurrentPath, setLang } from '../state/ui'
import { useTheme } from '../state/theme'
import { DEFAULT_USER, useSaveSetting, useSettings, useUser } from '../queries/user'
import { queryClient } from '../queries/client'
import { Button, Checkbox, Radio, RadioGroup, Select, Tabs, TextField, Typography } from '@iziplay/ui-kit'
import { KoboDevices } from '../components/koboDevices'

type SettingsTab = 'interface' | 'player' | 'kobo'

interface SettingsState {
  colorTheme: string
  font: string
  lang: Iziplay.Lang
  username: string
  mail: string
  oldPwd: string
  newPwd: string
  newPwd2: string
  providers: any[]
}

export const Settings = () => {
  // The tab lives in the URL so other pages can link straight to one
  // (books links to ?tab=kobo).
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const tab: SettingsTab = tabParam === 'player' || tabParam === 'kobo' ? tabParam : 'interface'

  const currentTheme = useTheme()
  const settings = useSettings().data
  const user = useUser().data ?? DEFAULT_USER
  const saveSetting = useSaveSetting()
  const [state, setState] = useState<SettingsState>({
    colorTheme: window.localStorage.getItem('color') || '#7E94F7',
    font: window.localStorage.getItem('font') ?? 'Netflix Sans',
    lang: getLang(),
    username: '',
    mail: '',
    oldPwd: '',
    newPwd: '',
    newPwd2: '',
    providers: []
  })

  function handle(
    field: Exclude<keyof SettingsState, 'providers'>,
    e: ChangeEvent<HTMLInputElement>
  ) {
    const obj: Partial<SettingsState> = {}
    if (field === 'lang') {
      if (isStringLanguage(e.target.value)) {
        obj[field] = e.target.value
      }
    } else {
      obj[field] = e.target.value
    }

    setState(oldState => ({
      ...oldState,
      ...obj
    }))
  }

  function changeLang(newLang: string) {
    if (isStringLanguage(newLang)) {
      setState(oldState => ({
        ...oldState,
        lang: newLang
      }))
      setLang(newLang)
      saveSetting.mutate({ lang: newLang })

      /*
       * The media lists are keyed by language, so switching it retires the old
       * cache entries outright rather than resetting four page counters.
       */
      queryClient.removeQueries({ queryKey: ['movies'] })
      queryClient.removeQueries({ queryKey: ['tvshows'] })
      queryClient.removeQueries({ queryKey: ['downloaded'] })
      queryClient.removeQueries({ queryKey: ['myhistory'] })
    }
  }

  function updateData() {
    iziplayAuth()
      .put('/me/', {
        username: state.username,
        mail: state.mail
      })
      .then(res => {
        displayNotification(res.data.msg, 'success')
        queryClient.invalidateQueries({ queryKey: ['user'] })
        setState(oldState => ({
          ...oldState,
          username: '',
          mail: ''
        }))
      })
      .catch(err => {
        if (err.response) {
          displayNotification(err.response.data.err, 'error')
        } else {
          displayNotification('An error occured, sorry!', 'error')
        }
      })
  }

  function password() {
    if (state.newPwd !== state.newPwd2) {
      displayNotification('New password not equal', 'error')
      return
    }
    iziplayAuth()
      .put('/me/password', {
        oldPassword: state.oldPwd,
        password: state.newPwd
      })
      .then(() => {
        setState(oldState => ({
          ...oldState,
          oldPwd: '',
          newPwd: '',
          newPwd2: ''
        }))

        displayNotification('Your new password is so... cool!', 'success')
      })
      .catch(err => {
        displayNotification(err.response.data.err, 'error')
      })
  }

  function theme(value: string) {
    if (isStringTheme(value)) {
      saveTheme(value)
    }
  }

  function changeColor(color: SettingsState['colorTheme']) {
    setState(oldState => ({
      ...oldState,
      colorTheme: color
    }))

    changeInterfaceColor(color)
  }

  function changeFont(font: string) {
    setState(oldState => ({
      ...oldState,
      font
    }))

    setFont(font)
  }

  function changeProviders(name: string) {
    const providers = { ...settings?.providers }
    providers[name] = !providers[name]
    saveSetting.mutate({ providers })
  }

  function changeSubtitles(code: (typeof languages)[0]['code']) {
    const current = settings?.subtitles ?? []
    const subtitles = current.includes(code)
      ? current.filter(entry => entry !== code)
      : [...current, code]
    saveSetting.mutate({ subtitles })
  }

  useEffect(() => {
    setCurrentPath([])
  }, [])

  return (
    <div className="settings scrollable">
      <Tabs<SettingsTab>
        value={tab}
        onChange={next => setSearchParams({ tab: next })}
        className="settings-tabs"
        tabs={[
          { value: 'interface', label: 'Interface' },
          { value: 'player', label: 'Video player' },
          { value: 'kobo', label: 'Kobo devices' }
        ]}
      />
      {tab === 'interface' && (
      <div className="settings-columns">
      <div className="settings-column">
      <div className="box">
        <div className="title">Profile</div>
        <div className="element">
          To modify your avatar, go to{' '}
          <a href="https://fr.gravatar.com/" target="blank_">
            Gravatar
          </a>
          .<br />
          <TextField
            type="text"
            placeholder="Username"
            value={state.username}
            onChange={e => handle('username', e)}
          />
          <TextField
            type="email"
            placeholder="Email"
            value={state.mail}
            onChange={e => handle('mail', e)}
          />
          <Button variant="contained" color="primary" onClick={updateData}>Save</Button>
        </div>
      </div>
      <div className="box">
        <div className="title">Theme</div>
        <div className="element">
          <div>
            <RadioGroup value={currentTheme} onChange={theme} className="theme-radios">
              <Radio value="light" label="Light" />
              <Radio value="dark" label="Dark" />
              <Radio value="auto" label="Auto (switch to dark when night)" />
            </RadioGroup>

            <br /><br />

            <Typography id="color-input-label" variant="subtitle2">Color</Typography>
            <input
              type="color"
              aria-labelledby="color-input-label"
              name="colorTheme"
              value={state.colorTheme}
              onChange={e => changeColor(e.target.value)}
            />

            <br /><br />
            <Select
              id="font-select"
              label="Font"
              value={state.font}
              onChange={changeFont}
              options={[
                { value: 'Netflix Sans', label: 'Netflix Sans' },
                { value: 'Noto Sans', label: 'Noto Sans' }
              ]}
            />
          </div>
        </div>
      </div>
      <div className="box">
        <div className="title">Language</div>
        <div className="element">
          <div>
            <Select
              id="lang-select"
              value={state.lang}
              onChange={changeLang}
              options={utils.langs.map(lang => ({ value: lang, label: lang }))}
            />
          </div>
        </div>
      </div>
      </div>
      <div className="settings-column">
      <div className="box">
        <div className="title">Third-party services</div>
        <br />
        <button
          className="oauth-button trakt-button"
          onClick={() => {
            window.open(
              'https://trakt.tv/oauth/authorize?client_id=806f276c7e5e5020be88962113493e219382a31184d2ed54a491cdc46a41c7df&redirect_uri=https%3A%2F%2Fauth.iziplay.fruitice.fr%2Foauth%2Ftrakt&response_type=code'
            )
          }}
        >
          <img src="/img/services/trakt.png" />
          {user.trakt ? 'Trakt already connected' : 'Connect Trakt'}
        </button>
      </div>
      <div className="box">
        <div className="title">Password</div>
        <div className="element">
          <TextField
            type="password"
            placeholder="Old password"
            value={state.oldPwd}
            onChange={e => handle('oldPwd', e)}
          />
          <TextField
            type="password"
            placeholder="New password"
            value={state.newPwd}
            onChange={e => handle('newPwd', e)}
          />
          <TextField
            type="password"
            placeholder="Confirm new password"
            value={state.newPwd2}
            onChange={e => handle('newPwd2', e)}
          />
          <Button variant="contained" color="primary" onClick={() => password()}>Save</Button>
        </div>
      </div>
      </div>
      </div>
      )}
      {tab === 'player' && (
      <div className="settings-columns">
      <div className="settings-column">
      <div className="box">
        <div className="title">Providers</div>
        <div className="element">
          <div>
            {Object.keys((settings?.providers ?? {})).map(prov => (
              <div key={prov} style={{ marginBottom: 6 }}>
                <Checkbox
                  name={prov}
                  onChange={() => changeProviders(prov)}
                  defaultChecked={(settings?.providers ?? {})[prov]}
                  label={prov}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
      <div className="settings-column">
      <div className="box">
        <div className="title">Subtitles</div>
        <div className="element">
          <div>
            {languages.map(lang => (
              <div key={`sub_${lang.code}`} style={{ marginBottom: 6 }}>
                <Checkbox
                  name={lang.code}
                  onChange={() => changeSubtitles(lang.code)}
                  defaultChecked={(settings?.subtitles ?? []).indexOf(lang.code) !== -1}
                  label={`${lang.name} (${lang.local})`}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
      </div>
      )}
      {tab === 'kobo' && (
      <div className="settings-columns">
      <div className="settings-column">
      <div className="box">
        <div className="title">Kobo e-readers</div>
        <div className="element">
          <KoboDevices />
        </div>
      </div>
      </div>
      </div>
      )}
    </div>
  )
}
